import React, { useEffect, useState } from 'react';
import { dashboardAPI } from '../../services/api';
import { TrendingUp, TrendingDown, Minus, RefreshCw } from 'lucide-react';

/**
 * SummaryBar Component
 * Sticky horizontal bar showing key metrics for "This Hour"
 * Auto-refreshes every 30-60 minutes
 */
const SummaryBar = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadSummary = async () => {
    try {
      const res = await dashboardAPI.getSummary();
      setSummary(res.data);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Failed to load summary', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    // Auto-refresh every 45 minutes
    const interval = setInterval(loadSummary, 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatChange = (value) => {
    if (!value && value !== 0) return null;
    const absValue = Math.abs(value);
    const sign = value >= 0 ? '+' : '';
    return `${sign}${absValue.toFixed(2)}%`;
  };

  const getChangeIcon = (value) => {
    if (!value && value !== 0) return <Minus className="w-4 h-4 text-gray-400" />;
    if (value > 0) return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (value < 0) return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getChangeColor = (value) => {
    if (!value && value !== 0) return 'text-gray-500';
    if (value > 0) return 'text-red-600 dark:text-red-400';
    if (value < 0) return 'text-green-600 dark:text-green-400';
    return 'text-gray-500';
  };

  if (loading) {
    return (
      <div className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <div className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Oil Prices */}
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Oil:</span>
            <div className="flex items-center space-x-1">
              {getChangeIcon(summary.oilChange?.brent)}
              <span className={`text-sm font-bold ${getChangeColor(summary.oilChange?.brent)}`}>
                Brent {formatChange(summary.oilChange?.brent)}
              </span>
            </div>
            <span className="text-gray-400">|</span>
            <div className="flex items-center space-x-1">
              {getChangeIcon(summary.oilChange?.wti)}
              <span className={`text-sm font-bold ${getChangeColor(summary.oilChange?.wti)}`}>
                WTI {formatChange(summary.oilChange?.wti)}
              </span>
            </div>
          </div>

          {/* Vessel Traffic */}
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Vessels:</span>
            <div className="flex items-center space-x-1">
              {getChangeIcon(summary.vesselChange)}
              <span className={`text-sm font-bold ${getChangeColor(summary.vesselChange)}`}>
                {formatChange(summary.vesselChange)}
              </span>
            </div>
          </div>

          {/* Air Traffic */}
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Flights:</span>
            <div className="flex items-center space-x-1">
              {getChangeIcon(summary.flightChange)}
              <span className={`text-sm font-bold ${getChangeColor(summary.flightChange)}`}>
                {formatChange(summary.flightChange)}
              </span>
            </div>
          </div>

          {/* Latest Headline */}
          {summary.headlines && summary.headlines.length > 0 && (
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Latest:</span>
              <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                {summary.headlines[0].headline}
              </span>
            </div>
          )}

          {/* Refresh indicator */}
          {lastUpdate && (
            <button
              onClick={loadSummary}
              className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              title="Refresh data"
            >
              <RefreshCw className="w-3 h-3" />
              <span>{lastUpdate.toLocaleTimeString()}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryBar;

